// src/primitives/Panel.tsx
import React2, { useMemo } from "react";
import { observer } from "mobx-react-lite";

// src/hooks/useFurnitureStore.tsx
import React, { createContext, useContext } from "react";
import { Assembly } from "@fabrick/store";
import { jsx } from "react/jsx-runtime";
var FurnitureStoreContext = createContext(null);
var FurnitureStoreProvider = ({ children }) => {
  const [store] = React.useState(() => {
    const instance = new Assembly();
    return instance;
  });
  return /* @__PURE__ */ jsx(FurnitureStoreContext.Provider, { value: store, children });
};
var useFurnitureStore = () => {
  const store = useContext(FurnitureStoreContext);
  if (!store) {
    throw new Error("useFurnitureStore must be used within FurnitureStoreProvider");
  }
  return store;
};

// src/primitives/Panel.tsx
import { fromSchema } from "@fabrick/store";
import { MaterialType, WoodType, GeometryType } from "@fabrick/schema";
import { eulerToQuaternion } from "@fabrick/schema/utils";
import { v4 as uuid } from "uuid";

// src/primitives/Material.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var Material = ({
  materialProps,
  emissive,
  emissiveIntensity
}) => {
  const pbr = materialProps.pbr_properties || {
    roughness: 0.8,
    metalness: 0
  };
  return /* @__PURE__ */ jsx2(
    "meshStandardMaterial",
    {
      color: materialProps.color || "#FFFFFF",
      roughness: pbr.roughness,
      metalness: pbr.metalness,
      transparent: pbr.opacity !== void 0 && pbr.opacity < 1,
      opacity: pbr.opacity,
      emissive,
      emissiveIntensity
    }
  );
};

// src/primitives/Panel.tsx
import { Euler, Quaternion } from "three";

// src/lib/visibility.ts
var isComponentHidden = (component) => {
  let current = component;
  while (current) {
    if (current.layout?.hidden) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

// src/primitives/Panel.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Panel = observer((props) => {
  const store = useFurnitureStore();
  const componentId = React2.useMemo(() => props.id || uuid(), [props.id]);
  const registerWithStore = props.registerWithStore ?? true;
  const schema = useMemo(() => {
    const id = componentId;
    const material = {
      type: props.material || MaterialType.WOOD,
      subtype: props.materialVariant || WoodType.PLYWOOD,
      color: "#D2B48C",
      // Default wood color (tan)
      ...props.materialProps,
      pbr_properties: {
        roughness: 0.8,
        metalness: 0,
        ...props.materialProps?.pbr_properties
      }
    };
    const geometry = {
      type: GeometryType.BOX,
      width: props.width,
      height: props.height,
      depth: props.thickness
    };
    const rotation2 = props.rotation || [0, 0, 0];
    const quaternion = eulerToQuaternion(rotation2[0], rotation2[1], rotation2[2]);
    const component = {
      id,
      type: "panel",
      name: props.name,
      geometry,
      material,
      grain_direction: props.grainDirection,
      transform: {
        position: {
          x: props.position?.[0] || 0,
          y: props.position?.[1] || 0,
          z: props.position?.[2] || 0
        },
        rotation: quaternion,
        scale: {
          x: props.scale?.[0] || 1,
          y: props.scale?.[1] || 1,
          z: props.scale?.[2] || 1
        }
      }
    };
    if (props.edgeBanding) {
      component.edge_banding = {
        edges: props.edgeBandingEdges || ["top", "bottom", "left", "right"],
        thickness: props.edgeBandingThickness || 1e-3,
        material: {
          type: MaterialType.WOOD,
          subtype: WoodType.OAK,
          pbr_properties: { roughness: 0.7, metalness: 0 }
        }
      };
    }
    return component;
  }, [
    componentId,
    props.width,
    props.height,
    props.thickness,
    props.material,
    props.materialVariant,
    props.position,
    props.rotation,
    props.scale,
    props.edgeBanding
  ]);
  React2.useEffect(() => {
    if (!registerWithStore) return;
    if (!store.components.has(schema.id)) {
      const component = fromSchema(schema);
      store.addComponent(component);
    }
  }, [schema.id, store, registerWithStore, schema]);
  const observableComponent = store.components.get(schema.id);
  const width = observableComponent?.properties?.geometry?.width ?? props.width;
  const height = observableComponent?.properties?.geometry?.height ?? props.height;
  const thickness = observableComponent?.properties?.geometry?.depth ?? props.thickness;
  const position = observableComponent?.transform ? [
    observableComponent.transform.position.x,
    observableComponent.transform.position.y,
    observableComponent.transform.position.z
  ] : props.position ?? [0, 0, 0];
  const scale = observableComponent?.transform ? [
    observableComponent.transform.scale.x,
    observableComponent.transform.scale.y,
    observableComponent.transform.scale.z
  ] : props.scale ?? [1, 1, 1];
  const rotation = observableComponent?.transform ? (() => {
    const { x, y, z, w } = observableComponent.transform.rotation;
    const euler = new Euler().setFromQuaternion(new Quaternion(x, y, z, w), "XYZ");
    return [euler.x, euler.y, euler.z];
  })() : props.rotation ?? [0, 0, 0];
  const materialProps = observableComponent?.properties?.material ?? schema.material;
  const isSelected = store.selectedComponentIds.has(schema.id);
  const isHovered = store.hoveredComponentId === schema.id;
  const hidden = observableComponent ? isComponentHidden(observableComponent) : false;
  return /* @__PURE__ */ jsxs("group", { position, rotation, scale, visible: !hidden, children: [
    /* @__PURE__ */ jsxs(
      "mesh",
      {
        castShadow: true,
        receiveShadow: true,
        onClick: (e) => {
          e.stopPropagation();
          store.selectComponent(schema.id, e.shiftKey);
          props.onClick?.(schema);
        },
        onPointerOver: (e) => {
          e.stopPropagation();
          store.setHoveredComponent(schema.id);
          props.onHover?.(schema);
        },
        onPointerOut: (e) => {
          e.stopPropagation();
          store.setHoveredComponent(null);
          props.onHover?.(null);
        },
        children: [
          /* @__PURE__ */ jsx3("boxGeometry", { args: [width, height, thickness] }),
          /* @__PURE__ */ jsx3(
            Material,
            {
              materialProps,
              emissive: isSelected ? "#4444ff" : isHovered ? "#444444" : void 0,
              emissiveIntensity: isSelected ? 0.3 : isHovered ? 0.1 : 0
            }
          )
        ]
      }
    ),
    isSelected && /* @__PURE__ */ jsxs("mesh", { children: [
      /* @__PURE__ */ jsx3("boxGeometry", { args: [width + 0.01, height + 0.01, thickness + 0.01] }),
      /* @__PURE__ */ jsx3("meshBasicMaterial", { color: "#4444ff", wireframe: true })
    ] })
  ] });
});

// src/primitives/Group.tsx
import { observer as observer2 } from "mobx-react-lite";
import { jsx as jsx4 } from "react/jsx-runtime";
var Group = observer2(({ component, children }) => {
  const transform = component.layout?.transform || component.transform;
  if (!transform) {
    return /* @__PURE__ */ jsx4("group", { children });
  }
  const { position, rotation, scale } = transform;
  return /* @__PURE__ */ jsx4(
    "group",
    {
      position: [position.x, position.y, position.z],
      quaternion: [rotation.x, rotation.y, rotation.z, rotation.w],
      scale: [scale.x, scale.y, scale.z],
      children
    }
  );
});

// src/custom/SimpleCabinet.tsx
import { useEffect, useMemo as useMemo2, useRef } from "react";
import { observer as observer3 } from "mobx-react-lite";
import {
  MaterialType as MaterialType2,
  WoodType as WoodType2,
  GeometryType as GeometryType2,
  ManufacturingOperationType
} from "@fabrick/schema";
import { eulerToQuaternion as eulerToQuaternion2 } from "@fabrick/schema/utils";
import { fromSchema as fromSchema2 } from "@fabrick/store";
import { v4 as uuid2 } from "uuid";
import { Euler as Euler2, Quaternion as Quaternion2 } from "three";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var PANEL_THICKNESS = 0.018;
var createCabinetIds = () => ({
  cabinetId: uuid2(),
  leftSideId: uuid2(),
  rightSideId: uuid2(),
  topPanelId: uuid2(),
  bottomPanelId: uuid2(),
  backPanelId: uuid2(),
  shelfPanelId: uuid2(),
  doorGroupId: uuid2(),
  doorPanelId: uuid2()
});
var createMaterial = (material, overrides = {}) => ({
  type: MaterialType2.WOOD,
  subtype: material,
  color: overrides.color ?? "#D2B48C",
  pbr_properties: {
    roughness: 0.8,
    metalness: 0
  }
});
var createGeometry = (width, height, depth) => ({
  type: GeometryType2.BOX,
  width,
  height,
  depth
});
var quaternionToEuler = (rotation) => {
  const euler = new Euler2().setFromQuaternion(new Quaternion2(rotation.x, rotation.y, rotation.z, rotation.w), "XYZ");
  return [euler.x, euler.y, euler.z];
};
var createCabinetSchema = (ids, config, position, name) => {
  const { width, height, depth, material, doorOpen } = config;
  const groupTransform = {
    position: { x: position[0], y: position[1], z: position[2] },
    rotation: { x: 0, y: 0, z: 0, w: 1 },
    scale: { x: 1, y: 1, z: 1 }
  };
  const carcassMaterial = createMaterial(material);
  const doorMaterial = createMaterial(material, { color: "#B8860B" });
  const children = {
    leftSide: {
      id: ids.leftSideId,
      type: "panel",
      metadata: { name: "Left Side" },
      geometry: createGeometry(depth, height, PANEL_THICKNESS),
      material: carcassMaterial,
      grain_direction: "length",
      edge_banding: {
        edges: ["top", "bottom"],
        thickness: 1e-3,
        material: createMaterial(WoodType2.OAK)
      },
      transform: {
        position: { x: -width / 2 + PANEL_THICKNESS / 2, y: height / 2, z: 0 },
        rotation: eulerToQuaternion2(0, Math.PI / 2, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    rightSide: {
      id: ids.rightSideId,
      type: "panel",
      metadata: { name: "Right Side" },
      geometry: createGeometry(depth, height, PANEL_THICKNESS),
      material: carcassMaterial,
      grain_direction: "length",
      edge_banding: {
        edges: ["top", "bottom"],
        thickness: 1e-3,
        material: createMaterial(WoodType2.OAK)
      },
      transform: {
        position: { x: width / 2 - PANEL_THICKNESS / 2, y: height / 2, z: 0 },
        rotation: eulerToQuaternion2(0, Math.PI / 2, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    top: {
      id: ids.topPanelId,
      type: "panel",
      metadata: { name: "Top" },
      geometry: createGeometry(width, depth, PANEL_THICKNESS),
      material: carcassMaterial,
      grain_direction: "length",
      edge_banding: {
        edges: ["left", "right"],
        thickness: 1e-3,
        material: createMaterial(WoodType2.OAK)
      },
      transform: {
        position: { x: 0, y: height, z: 0 },
        rotation: eulerToQuaternion2(Math.PI / 2, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    bottom: {
      id: ids.bottomPanelId,
      type: "panel",
      metadata: { name: "Bottom" },
      geometry: createGeometry(width, depth, PANEL_THICKNESS),
      material: carcassMaterial,
      grain_direction: "length",
      edge_banding: {
        edges: ["left", "right"],
        thickness: 1e-3,
        material: createMaterial(WoodType2.OAK)
      },
      transform: {
        position: { x: 0, y: 0, z: 0 },
        rotation: eulerToQuaternion2(Math.PI / 2, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    back: {
      id: ids.backPanelId,
      type: "panel",
      metadata: { name: "Back" },
      geometry: createGeometry(width - PANEL_THICKNESS * 2, height - PANEL_THICKNESS, PANEL_THICKNESS / 2),
      material: carcassMaterial,
      grain_direction: "length",
      transform: {
        position: { x: 0, y: height / 2, z: -depth / 2 + PANEL_THICKNESS / 4 },
        rotation: eulerToQuaternion2(0, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    shelf: {
      id: ids.shelfPanelId,
      type: "panel",
      metadata: { name: "Shelf" },
      geometry: createGeometry(width - PANEL_THICKNESS * 2, depth - PANEL_THICKNESS * 2, PANEL_THICKNESS),
      material: carcassMaterial,
      grain_direction: "length",
      edge_banding: {
        edges: ["top"],
        thickness: 1e-3,
        material: createMaterial(WoodType2.OAK)
      },
      transform: {
        position: { x: 0, y: height / 2, z: 0 },
        rotation: eulerToQuaternion2(Math.PI / 2, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    door: {
      id: ids.doorGroupId,
      type: "group",
      metadata: { name: "Door" },
      transform: {
        position: {
          x: -width / 2 + PANEL_THICKNESS,
          y: height / 2,
          z: depth / 2 + PANEL_THICKNESS / 2
        },
        rotation: eulerToQuaternion2(0, doorOpen, 0),
        scale: { x: 1, y: 1, z: 1 }
      },
      children: {
        panel: {
          id: ids.doorPanelId,
          type: "panel",
          metadata: { name: "Door Panel" },
          geometry: createGeometry(width - PANEL_THICKNESS * 2, height - PANEL_THICKNESS * 2, PANEL_THICKNESS),
          material: doorMaterial,
          grain_direction: "length",
          transform: {
            position: {
              x: (width - PANEL_THICKNESS * 2) / 2,
              y: 0,
              z: 0
            },
            rotation: eulerToQuaternion2(0, 0, 0),
            scale: { x: 1, y: 1, z: 1 }
          }
        }
      }
    }
  };
  const manufacturingSteps = [
    {
      id: uuid2(),
      operation: ManufacturingOperationType.CUT,
      order: 1,
      name: "Cut panels to size",
      description: "Cut all plywood panels to specified dimensions",
      tool: "Table saw",
      estimated_time: 30,
      notes: "Ensure all cuts are square and accurate"
    },
    {
      id: uuid2(),
      operation: ManufacturingOperationType.EDGE_BAND,
      order: 2,
      name: "Apply edge banding",
      description: "Apply edge banding to all visible edges",
      tool: "Edge bander or iron",
      estimated_time: 45,
      notes: "Use matching edge banding material"
    },
    {
      id: uuid2(),
      operation: ManufacturingOperationType.DRILL,
      order: 3,
      name: "Drill shelf pin holes",
      description: "Drill holes for adjustable shelf pins in side panels",
      tool: "Drill press with jig",
      estimated_time: 20,
      parameters: {
        diameter: 5,
        depth: 12,
        bit_type: "brad_point"
      }
    },
    {
      id: uuid2(),
      operation: ManufacturingOperationType.SAND,
      order: 4,
      name: "Sand all surfaces",
      description: "Sand all panel surfaces smooth",
      tool: "Random orbital sander",
      estimated_time: 25,
      parameters: {
        grit: 220,
        all_faces: true
      }
    },
    {
      id: uuid2(),
      operation: ManufacturingOperationType.ASSEMBLE,
      order: 5,
      name: "Assemble cabinet box",
      description: "Assemble sides, top, bottom, and back panel",
      tool: "Pocket hole jig, clamps",
      estimated_time: 40,
      notes: "Check for square before glue dries"
    },
    {
      id: uuid2(),
      operation: ManufacturingOperationType.FINISH,
      order: 6,
      name: "Apply finish",
      description: "Apply protective finish to all surfaces",
      tool: "Spray gun or brush",
      estimated_time: 60,
      parameters: {
        finish_type: "polyurethane",
        coats: 3
      },
      notes: "Allow proper drying time between coats"
    },
    {
      id: uuid2(),
      operation: ManufacturingOperationType.ASSEMBLE,
      order: 7,
      name: "Install door and hardware",
      description: "Attach hinges and mount door to cabinet",
      tool: "Screwdriver, drill",
      estimated_time: 20,
      notes: "Adjust hinges for proper alignment"
    }
  ];
  return {
    id: ids.cabinetId,
    type: "group",
    metadata: {
      name,
      componentType: "simple_cabinet",
      config
    },
    transform: groupTransform,
    children,
    manufacturing_steps: manufacturingSteps
  };
};
var applyCabinetConfig = (store, ids, config, position) => {
  const { width, height, depth, material, doorOpen } = config;
  const carcassMaterial = createMaterial(material);
  const doorMaterial = createMaterial(material, { color: "#B8860B" });
  const setTransform = (componentId, transform) => {
    const component = store.components.get(componentId);
    if (!component) return;
    component.setTransform(transform);
  };
  const setGeometry = (componentId, geometry) => {
    const component = store.components.get(componentId);
    if (!component || component.type !== "panel") return;
    const panel = component;
    if (panel.setGeometry) {
      panel.setGeometry(geometry);
    } else if (panel.properties) {
      panel.properties.geometry = geometry;
    }
  };
  const setMaterial = (componentId, mat) => {
    const component = store.components.get(componentId);
    if (!component || component.type !== "panel") return;
    const panel = component;
    if (panel.setMaterial) {
      panel.setMaterial(mat);
    } else if (panel.properties) {
      panel.properties.material = mat;
    }
  };
  setTransform(ids.cabinetId, {
    position,
    rotation: { x: 0, y: 0, z: 0, w: 1 },
    scale: { x: 1, y: 1, z: 1 }
  });
  setGeometry(ids.leftSideId, createGeometry(depth, height, PANEL_THICKNESS));
  setTransform(ids.leftSideId, {
    position: {
      x: -width / 2 + PANEL_THICKNESS / 2,
      y: height / 2,
      z: 0
    },
    rotation: eulerToQuaternion2(0, Math.PI / 2, 0)
  });
  setMaterial(ids.leftSideId, carcassMaterial);
  setGeometry(ids.rightSideId, createGeometry(depth, height, PANEL_THICKNESS));
  setTransform(ids.rightSideId, {
    position: {
      x: width / 2 - PANEL_THICKNESS / 2,
      y: height / 2,
      z: 0
    },
    rotation: eulerToQuaternion2(0, Math.PI / 2, 0)
  });
  setMaterial(ids.rightSideId, carcassMaterial);
  setGeometry(ids.topPanelId, createGeometry(width, depth, PANEL_THICKNESS));
  setTransform(ids.topPanelId, {
    position: { x: 0, y: height, z: 0 },
    rotation: eulerToQuaternion2(Math.PI / 2, 0, 0)
  });
  setMaterial(ids.topPanelId, carcassMaterial);
  setGeometry(ids.bottomPanelId, createGeometry(width, depth, PANEL_THICKNESS));
  setTransform(ids.bottomPanelId, {
    position: { x: 0, y: 0, z: 0 },
    rotation: eulerToQuaternion2(Math.PI / 2, 0, 0)
  });
  setMaterial(ids.bottomPanelId, carcassMaterial);
  setGeometry(ids.backPanelId, createGeometry(width - PANEL_THICKNESS * 2, height - PANEL_THICKNESS, PANEL_THICKNESS / 2));
  setTransform(ids.backPanelId, {
    position: { x: 0, y: height / 2, z: -depth / 2 + PANEL_THICKNESS / 4 },
    rotation: eulerToQuaternion2(0, 0, 0)
  });
  setMaterial(ids.backPanelId, carcassMaterial);
  setGeometry(ids.shelfPanelId, createGeometry(width - PANEL_THICKNESS * 2, depth - PANEL_THICKNESS * 2, PANEL_THICKNESS));
  setTransform(ids.shelfPanelId, {
    position: { x: 0, y: height / 2, z: 0 },
    rotation: eulerToQuaternion2(Math.PI / 2, 0, 0)
  });
  setMaterial(ids.shelfPanelId, carcassMaterial);
  setTransform(ids.doorGroupId, {
    position: {
      x: -width / 2 + PANEL_THICKNESS,
      y: height / 2,
      z: depth / 2 + PANEL_THICKNESS / 2
    },
    rotation: eulerToQuaternion2(0, doorOpen, 0)
  });
  setGeometry(ids.doorPanelId, createGeometry(width - PANEL_THICKNESS * 2, height - PANEL_THICKNESS * 2, PANEL_THICKNESS));
  setTransform(ids.doorPanelId, {
    position: {
      x: (width - PANEL_THICKNESS * 2) / 2,
      y: 0,
      z: 0
    },
    rotation: eulerToQuaternion2(0, 0, 0)
  });
  setMaterial(ids.doorPanelId, doorMaterial);
};
var SimpleCabinet = observer3((props) => {
  const store = useFurnitureStore();
  const idsRef = useRef();
  if (!idsRef.current) {
    idsRef.current = createCabinetIds();
  }
  const ids = idsRef.current;
  const defaults = useMemo2(() => ({
    width: props.width ?? 0.8,
    height: props.height ?? 1,
    depth: props.depth ?? 0.5,
    material: props.material ?? WoodType2.PLYWOOD,
    doorOpen: props.doorOpen ?? 0
  }), [props.width, props.height, props.depth, props.material, props.doorOpen]);
  const posX = props.position?.[0] ?? 0;
  const posY = props.position?.[1] ?? 0;
  const posZ = props.position?.[2] ?? 0;
  const position = [posX, posY, posZ];
  const name = props.name ?? "Simple Cabinet";
  useEffect(() => {
    if (!store.components.has(ids.cabinetId)) {
      const schema = createCabinetSchema(ids, defaults, position, name);
      const component = fromSchema2(schema);
      store.addComponent(component);
    }
  }, [store, ids, defaults, posX, posY, posZ, name]);
  const cabinetComponent = store.components.get(ids.cabinetId);
  const config = cabinetComponent?.metadata?.config ?? defaults;
  useEffect(() => {
    if (!cabinetComponent) return;
    if (!cabinetComponent.metadata?.config) {
      if (!cabinetComponent.metadata) {
        cabinetComponent.metadata = {};
      }
      cabinetComponent.metadata.config = { ...config };
      return;
    }
    applyCabinetConfig(store, ids, config, {
      x: posX,
      y: posY,
      z: posZ
    });
  }, [store, ids, cabinetComponent, config.width, config.height, config.depth, config.material, config.doorOpen, posX, posY, posZ]);
  if (!cabinetComponent) {
    return null;
  }
  const hidden = isComponentHidden(cabinetComponent);
  const doorGroup = store.components.get(ids.doorGroupId);
  const doorGroupPosition = doorGroup && doorGroup.transform ? [
    doorGroup.transform.position.x,
    doorGroup.transform.position.y,
    doorGroup.transform.position.z
  ] : [
    -config.width / 2 + PANEL_THICKNESS,
    config.height / 2,
    config.depth / 2 + PANEL_THICKNESS / 2
  ];
  const doorGroupRotation = doorGroup && doorGroup.transform ? quaternionToEuler(doorGroup.transform.rotation) : [0, config.doorOpen, 0];
  return /* @__PURE__ */ jsxs2("group", { position, visible: !hidden, name, children: [
    /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.leftSideId,
        width: config.depth,
        height: config.height,
        thickness: PANEL_THICKNESS,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [-config.width / 2 + PANEL_THICKNESS / 2, config.height / 2, 0],
        rotation: [0, Math.PI / 2, 0],
        edgeBanding: true,
        edgeBandingEdges: ["top", "bottom"],
        registerWithStore: false
      }
    ),
    /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.rightSideId,
        width: config.depth,
        height: config.height,
        thickness: PANEL_THICKNESS,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [config.width / 2 - PANEL_THICKNESS / 2, config.height / 2, 0],
        rotation: [0, Math.PI / 2, 0],
        edgeBanding: true,
        edgeBandingEdges: ["top", "bottom"],
        registerWithStore: false
      }
    ),
    /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.topPanelId,
        width: config.width,
        height: config.depth,
        thickness: PANEL_THICKNESS,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [0, config.height, 0],
        rotation: [Math.PI / 2, 0, 0],
        edgeBanding: true,
        edgeBandingEdges: ["left", "right"],
        registerWithStore: false
      }
    ),
    /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.bottomPanelId,
        width: config.width,
        height: config.depth,
        thickness: PANEL_THICKNESS,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [0, 0, 0],
        rotation: [Math.PI / 2, 0, 0],
        edgeBanding: true,
        edgeBandingEdges: ["left", "right"],
        registerWithStore: false
      }
    ),
    /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.backPanelId,
        width: config.width - PANEL_THICKNESS * 2,
        height: config.height - PANEL_THICKNESS,
        thickness: PANEL_THICKNESS / 2,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [0, config.height / 2, -config.depth / 2 + PANEL_THICKNESS / 4],
        registerWithStore: false
      }
    ),
    /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.shelfPanelId,
        width: config.width - PANEL_THICKNESS * 2,
        height: config.depth - PANEL_THICKNESS * 2,
        thickness: PANEL_THICKNESS,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [0, config.height / 2, 0],
        rotation: [Math.PI / 2, 0, 0],
        edgeBanding: true,
        edgeBandingEdges: ["top"],
        registerWithStore: false
      }
    ),
    /* @__PURE__ */ jsx5("group", { position: doorGroupPosition, rotation: doorGroupRotation, children: /* @__PURE__ */ jsx5(
      Panel,
      {
        id: ids.doorPanelId,
        width: config.width - PANEL_THICKNESS * 2,
        height: config.height - PANEL_THICKNESS * 2,
        thickness: PANEL_THICKNESS,
        material: MaterialType2.WOOD,
        materialVariant: config.material,
        position: [(config.width - PANEL_THICKNESS * 2) / 2, 0, 0],
        registerWithStore: false
      }
    ) })
  ] });
});
var SimpleCabinetDefinition = {
  id: "simple_cabinet",
  name: "Simple Cabinet",
  category: "storage",
  description: "Basic cabinet with one shelf and door",
  defaultProps: {
    width: 0.8,
    height: 1,
    depth: 0.5,
    material: WoodType2.PLYWOOD,
    doorOpen: 0
  },
  propsSchema: {
    width: {
      type: "number",
      label: "Width",
      default: 0.8,
      min: 0.3,
      max: 2,
      step: 0.05,
      unit: "m"
    },
    height: {
      type: "number",
      label: "Height",
      default: 1,
      min: 0.5,
      max: 2.5,
      step: 0.05,
      unit: "m"
    },
    depth: {
      type: "number",
      label: "Depth",
      default: 0.5,
      min: 0.2,
      max: 1,
      step: 0.05,
      unit: "m"
    },
    material: {
      type: "enum",
      label: "Material",
      default: WoodType2.PLYWOOD,
      options: Object.values(WoodType2)
    },
    doorOpen: {
      type: "number",
      label: "Door Open Angle",
      default: 0,
      min: 0,
      max: Math.PI / 2,
      step: 0.1,
      unit: "rad"
    }
  },
  factory: (props) => {
    const ids = createCabinetIds();
    const config = {
      width: props.width ?? 0.8,
      height: props.height ?? 1,
      depth: props.depth ?? 0.5,
      material: props.material ?? WoodType2.PLYWOOD,
      doorOpen: props.doorOpen ?? 0
    };
    return createCabinetSchema(ids, config, [0, 0, 0], "Simple Cabinet");
  },
  ReactComponent: SimpleCabinet
};
var SimpleCabinetFactory = {
  createSchema: (position = [0, 0, 0], config) => {
    const fullConfig = {
      width: 0.6,
      height: 0.9,
      depth: 0.5,
      material: WoodType2.PLYWOOD,
      doorOpen: 0,
      ...config
    };
    const ids = createCabinetIds();
    return createCabinetSchema(ids, fullConfig, position, "Simple Cabinet");
  },
  ReactComponent: SimpleCabinet
};

// src/custom/Door.tsx
import { useEffect as useEffect2, useMemo as useMemo3 } from "react";
import { observer as observer4 } from "mobx-react-lite";
import {
  WoodType as WoodType3,
  ManufacturingOperationType as ManufacturingOperationType2
} from "@fabrick/schema";
import { eulerToQuaternion as eulerToQuaternion3 } from "@fabrick/schema/utils";
import { fromSchema as fromSchema3 } from "@fabrick/store";
import { v4 as uuid3 } from "uuid";
import { jsx as jsx6 } from "react/jsx-runtime";
var PANEL_THICKNESS2 = 0.018;
var createDoorSchema = (id, config, position, name) => {
  const { width, height, thickness, material, style } = config;
  const manufacturingSteps = [
    {
      id: uuid3(),
      operation: ManufacturingOperationType2.CUT,
      order: 1,
      name: "Cut door panel",
      description: "Cut door panel to specified dimensions",
      tool: "Table saw",
      estimated_time: 15,
      notes: "Ensure square cuts"
    },
    {
      id: uuid3(),
      operation: ManufacturingOperationType2.ROUTE,
      order: 2,
      name: style === "shaker" ? "Route shaker profile" : "Route edges",
      description: style === "shaker" ? "Create shaker-style panel with routed profile" : "Round over edges",
      tool: "Router with appropriate bit",
      estimated_time: 30,
      notes: style === "shaker" ? "Use shaker panel bit set" : 'Use 1/4" roundover bit'
    },
    {
      id: uuid3(),
      operation: ManufacturingOperationType2.SAND,
      order: 3,
      name: "Sand door",
      description: "Sand all surfaces smooth",
      tool: "Random orbital sander",
      estimated_time: 15,
      parameters: {
        grit: 220,
        all_faces: true
      }
    },
    {
      id: uuid3(),
      operation: ManufacturingOperationType2.DRILL,
      order: 4,
      name: "Drill hinge holes",
      description: "Drill 35mm hinge cup holes",
      tool: "Forstner bit (35mm)",
      estimated_time: 10,
      parameters: {
        diameter: 35,
        depth: 12,
        bit_type: "forstner"
      }
    },
    {
      id: uuid3(),
      operation: ManufacturingOperationType2.FINISH,
      order: 5,
      name: "Apply finish",
      description: "Apply protective finish",
      tool: "Spray gun",
      estimated_time: 45,
      parameters: {
        finish_type: "polyurethane",
        coats: 2
      }
    }
  ];
  return {
    id,
    type: "panel",
    metadata: {
      name,
      componentType: "door",
      config,
      style
    },
    geometry: {
      type: "box",
      width,
      height,
      depth: thickness
    },
    material: {
      type: "wood",
      subtype: material,
      color: material === WoodType3.OAK ? "#D2B48C" : "#8B7355"
    },
    grain_direction: "length",
    transform: {
      position: { x: position[0], y: position[1], z: position[2] },
      rotation: eulerToQuaternion3(0, 0, 0),
      scale: { x: 1, y: 1, z: 1 }
    },
    manufacturing_steps: manufacturingSteps
  };
};
var DoorComponent = observer4(({
  position = [0, 0, 0],
  name = "Door",
  config: configOverride = {}
}) => {
  const store = useFurnitureStore();
  const id = useMemo3(() => uuid3(), []);
  const defaults = {
    width: 0.4,
    height: 0.7,
    thickness: PANEL_THICKNESS2,
    material: WoodType3.OAK,
    style: "flat",
    hingeSide: "left",
    ...configOverride
  };
  useEffect2(() => {
    if (!store.components.has(id)) {
      const schema = createDoorSchema(id, defaults, position, name);
      const component = fromSchema3(schema);
      store.addComponent(component);
    }
  }, [store, id, name]);
  const doorComponent = store.components.get(id);
  if (!doorComponent) return null;
  return /* @__PURE__ */ jsx6(Panel, { component: doorComponent });
});
var Door = {
  createSchema: (position = [0, 0, 0], config) => {
    const fullConfig = {
      width: 0.4,
      height: 0.7,
      thickness: PANEL_THICKNESS2,
      material: WoodType3.OAK,
      style: "flat",
      hingeSide: "left",
      ...config
    };
    return createDoorSchema(uuid3(), fullConfig, position, "Door");
  },
  ReactComponent: DoorComponent
};

// src/custom/Drawer.tsx
import { useEffect as useEffect3, useMemo as useMemo4 } from "react";
import { observer as observer5 } from "mobx-react-lite";
import {
  WoodType as WoodType4,
  ManufacturingOperationType as ManufacturingOperationType3
} from "@fabrick/schema";
import { eulerToQuaternion as eulerToQuaternion4 } from "@fabrick/schema/utils";
import { fromSchema as fromSchema4 } from "@fabrick/store";
import { v4 as uuid4 } from "uuid";
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var PANEL_THICKNESS3 = 0.018;
var DRAWER_SIDE_HEIGHT = 0.12;
var createDrawerSchema = (ids, config, position, name) => {
  const { width, depth, height, material, bottomMaterial } = config;
  const manufacturingSteps = [
    {
      id: uuid4(),
      operation: ManufacturingOperationType3.CUT,
      order: 1,
      name: "Cut drawer parts",
      description: "Cut all drawer parts to size",
      tool: "Table saw",
      estimated_time: 25,
      notes: "Front, back, sides, and bottom"
    },
    {
      id: uuid4(),
      operation: ManufacturingOperationType3.ROUTE,
      order: 2,
      name: "Route dado for bottom",
      description: 'Route 1/4" dado groove for bottom panel',
      tool: "Router with dado bit",
      estimated_time: 15,
      parameters: {
        depth: 6,
        width: 6
      }
    },
    {
      id: uuid4(),
      operation: ManufacturingOperationType3.JOIN,
      order: 3,
      name: "Cut dovetail joints",
      description: "Cut dovetail joints for corners",
      tool: "Dovetail jig",
      estimated_time: 45,
      parameters: {
        joint_type: "dovetail"
      },
      notes: "Can use box joints as alternative"
    },
    {
      id: uuid4(),
      operation: ManufacturingOperationType3.SAND,
      order: 4,
      name: "Sand all parts",
      description: "Sand all drawer parts",
      tool: "Random orbital sander",
      estimated_time: 20,
      parameters: {
        grit: 220,
        all_faces: true
      }
    },
    {
      id: uuid4(),
      operation: ManufacturingOperationType3.ASSEMBLE,
      order: 5,
      name: "Assemble drawer box",
      description: "Glue and assemble drawer",
      tool: "Bar clamps",
      estimated_time: 30,
      notes: "Check for square, slide in bottom panel"
    },
    {
      id: uuid4(),
      operation: ManufacturingOperationType3.FINISH,
      order: 6,
      name: "Apply finish",
      description: "Apply finish to drawer",
      tool: "Brush or spray gun",
      estimated_time: 35,
      parameters: {
        finish_type: "polyurethane",
        coats: 2
      }
    }
  ];
  const materialProps = {
    type: "wood",
    subtype: material,
    color: "#D2B48C"
  };
  const bottomMaterialProps = {
    type: "wood",
    subtype: bottomMaterial,
    color: "#E8D5C4"
  };
  const children = {
    front: {
      id: ids.frontId,
      type: "panel",
      metadata: { name: "Drawer Front" },
      geometry: {
        type: "box",
        width,
        height,
        depth: PANEL_THICKNESS3
      },
      material: materialProps,
      grain_direction: "length",
      transform: {
        position: { x: 0, y: height / 2, z: depth / 2 },
        rotation: eulerToQuaternion4(0, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    back: {
      id: ids.backId,
      type: "panel",
      metadata: { name: "Drawer Back" },
      geometry: {
        type: "box",
        width: width - PANEL_THICKNESS3 * 2,
        height: DRAWER_SIDE_HEIGHT,
        depth: PANEL_THICKNESS3
      },
      material: materialProps,
      grain_direction: "length",
      transform: {
        position: { x: 0, y: DRAWER_SIDE_HEIGHT / 2, z: -depth / 2 + PANEL_THICKNESS3 },
        rotation: eulerToQuaternion4(0, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    leftSide: {
      id: ids.leftSideId,
      type: "panel",
      metadata: { name: "Left Side" },
      geometry: {
        type: "box",
        width: depth - PANEL_THICKNESS3,
        height: DRAWER_SIDE_HEIGHT,
        depth: PANEL_THICKNESS3
      },
      material: materialProps,
      grain_direction: "length",
      transform: {
        position: { x: -width / 2 + PANEL_THICKNESS3 / 2, y: DRAWER_SIDE_HEIGHT / 2, z: 0 },
        rotation: eulerToQuaternion4(0, Math.PI / 2, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    rightSide: {
      id: ids.rightSideId,
      type: "panel",
      metadata: { name: "Right Side" },
      geometry: {
        type: "box",
        width: depth - PANEL_THICKNESS3,
        height: DRAWER_SIDE_HEIGHT,
        depth: PANEL_THICKNESS3
      },
      material: materialProps,
      grain_direction: "length",
      transform: {
        position: { x: width / 2 - PANEL_THICKNESS3 / 2, y: DRAWER_SIDE_HEIGHT / 2, z: 0 },
        rotation: eulerToQuaternion4(0, Math.PI / 2, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    },
    bottom: {
      id: ids.bottomId,
      type: "panel",
      metadata: { name: "Bottom" },
      geometry: {
        type: "box",
        width: width - PANEL_THICKNESS3 * 2 - 0.012,
        height: depth - PANEL_THICKNESS3 * 2 - 0.012,
        depth: 6e-3
      },
      material: bottomMaterialProps,
      grain_direction: "length",
      transform: {
        position: { x: 0, y: 0.01, z: 0 },
        rotation: eulerToQuaternion4(Math.PI / 2, 0, 0),
        scale: { x: 1, y: 1, z: 1 }
      }
    }
  };
  return {
    id: ids.groupId,
    type: "group",
    metadata: {
      name,
      componentType: "drawer",
      config
    },
    transform: {
      position: { x: position[0], y: position[1], z: position[2] },
      rotation: { x: 0, y: 0, z: 0, w: 1 },
      scale: { x: 1, y: 1, z: 1 }
    },
    children,
    manufacturing_steps: manufacturingSteps
  };
};
var DrawerComponent = observer5(({
  position = [0, 0, 0],
  name = "Drawer",
  config: configOverride = {}
}) => {
  const store = useFurnitureStore();
  const ids = useMemo4(() => ({
    groupId: uuid4(),
    frontId: uuid4(),
    backId: uuid4(),
    leftSideId: uuid4(),
    rightSideId: uuid4(),
    bottomId: uuid4()
  }), []);
  const defaults = {
    width: 0.4,
    depth: 0.45,
    height: 0.15,
    material: WoodType4.MAPLE,
    bottomMaterial: WoodType4.PLYWOOD,
    ...configOverride
  };
  useEffect3(() => {
    if (!store.components.has(ids.groupId)) {
      const schema = createDrawerSchema(ids, defaults, position, name);
      const component = fromSchema4(schema);
      store.addComponent(component);
    }
  }, [store, ids, name]);
  const drawerGroup = store.components.get(ids.groupId);
  const front = store.components.get(ids.frontId);
  const back = store.components.get(ids.backId);
  const leftSide = store.components.get(ids.leftSideId);
  const rightSide = store.components.get(ids.rightSideId);
  const bottom = store.components.get(ids.bottomId);
  if (!drawerGroup) return null;
  return /* @__PURE__ */ jsxs3(Group, { component: drawerGroup, children: [
    front && /* @__PURE__ */ jsx7(Panel, { component: front }),
    back && /* @__PURE__ */ jsx7(Panel, { component: back }),
    leftSide && /* @__PURE__ */ jsx7(Panel, { component: leftSide }),
    rightSide && /* @__PURE__ */ jsx7(Panel, { component: rightSide }),
    bottom && /* @__PURE__ */ jsx7(Panel, { component: bottom })
  ] });
});
var Drawer = {
  createSchema: (position = [0, 0, 0], config) => {
    const fullConfig = {
      width: 0.4,
      depth: 0.45,
      height: 0.15,
      material: WoodType4.MAPLE,
      bottomMaterial: WoodType4.PLYWOOD,
      ...config
    };
    const ids = {
      groupId: uuid4(),
      frontId: uuid4(),
      backId: uuid4(),
      leftSideId: uuid4(),
      rightSideId: uuid4(),
      bottomId: uuid4()
    };
    return createDrawerSchema(ids, fullConfig, position, "Drawer");
  },
  ReactComponent: DrawerComponent
};

// src/custom/Hardware.tsx
import { useEffect as useEffect4, useMemo as useMemo5 } from "react";
import { observer as observer6 } from "mobx-react-lite";
import {
  ManufacturingOperationType as ManufacturingOperationType4
} from "@fabrick/schema";
import { eulerToQuaternion as eulerToQuaternion5 } from "@fabrick/schema/utils";
import { fromSchema as fromSchema5 } from "@fabrick/store";
import { v4 as uuid5 } from "uuid";
import { Box } from "@react-three/drei";
import { jsx as jsx8 } from "react/jsx-runtime";
var createHardwareSchema = (id, config, position, name) => {
  const { type, material, finish } = config;
  const manufacturingSteps = [
    {
      id: uuid5(),
      operation: ManufacturingOperationType4.DRILL,
      order: 1,
      name: `Drill ${type} mounting holes`,
      description: `Mark and drill pilot holes for ${type} installation`,
      tool: "Drill with pilot bit",
      estimated_time: 5,
      parameters: {
        diameter: type === "hinge" ? 2 : 3,
        depth: type === "hinge" ? 35 : 15
      }
    },
    {
      id: uuid5(),
      operation: ManufacturingOperationType4.ASSEMBLE,
      order: 2,
      name: `Install ${type}`,
      description: `Attach ${type} hardware with screws`,
      tool: "Screwdriver",
      estimated_time: type === "hinge" ? 10 : 5,
      notes: `Use ${material} ${finish} ${type}`
    }
  ];
  const getColor = () => {
    switch (material) {
      case "brass":
        return "#B5A642";
      case "chrome":
        return "#C0C0C0";
      case "nickel":
        return "#B0B0B0";
      case "black":
        return "#1a1a1a";
      case "stainless":
        return "#D4D4D4";
      default:
        return "#C0C0C0";
    }
  };
  const getDimensions = () => {
    switch (type) {
      case "hinge":
        return { width: 0.035, height: 0.075, depth: 0.012 };
      case "pull":
        return { width: 0.128, height: 0.025, depth: 0.02 };
      case "knob":
        return { width: 0.03, height: 0.03, depth: 0.025 };
      case "slide":
        return { width: 0.012, height: 0.35, depth: 0.012 };
      case "handle":
        return { width: 0.15, height: 0.02, depth: 0.03 };
      default:
        return { width: 0.03, height: 0.03, depth: 0.02 };
    }
  };
  const dims = getDimensions();
  return {
    id,
    type: "hardware",
    metadata: {
      name,
      componentType: "hardware",
      config,
      hardwareType: type
    },
    hardware_type: type,
    material: {
      type: "metal",
      subtype: material,
      color: getColor(),
      finish
    },
    dimensions: dims,
    transform: {
      position: { x: position[0], y: position[1], z: position[2] },
      rotation: eulerToQuaternion5(0, 0, 0),
      scale: { x: 1, y: 1, z: 1 }
    },
    manufacturing_steps: manufacturingSteps
  };
};
var HardwareComponent = observer6(({
  position = [0, 0, 0],
  name = "Hardware",
  config: configOverride = {}
}) => {
  const store = useFurnitureStore();
  const id = useMemo5(() => uuid5(), []);
  const defaults = {
    type: "pull",
    material: "chrome",
    finish: "brushed",
    ...configOverride
  };
  useEffect4(() => {
    if (!store.components.has(id)) {
      const schema = createHardwareSchema(id, defaults, position, name);
      const component = fromSchema5(schema);
      store.addComponent(component);
    }
  }, [store, id, name]);
  const hardwareComponent = store.components.get(id);
  if (!hardwareComponent) return null;
  const dims = hardwareComponent.dimensions || { width: 0.03, height: 0.03, depth: 0.02 };
  const color = hardwareComponent.material?.color || "#C0C0C0";
  const transform = hardwareComponent.layout?.transform || hardwareComponent.transform;
  return /* @__PURE__ */ jsx8(
    "group",
    {
      position: [transform.position.x, transform.position.y, transform.position.z],
      children: /* @__PURE__ */ jsx8(
        Box,
        {
          args: [dims.width, dims.height, dims.depth],
          castShadow: true,
          receiveShadow: true,
          children: /* @__PURE__ */ jsx8(
            "meshStandardMaterial",
            {
              color,
              metalness: 0.8,
              roughness: hardwareComponent.material?.finish === "polished" ? 0.1 : 0.4
            }
          )
        }
      )
    }
  );
});
var Hardware = {
  createSchema: (position = [0, 0, 0], config) => {
    const fullConfig = {
      type: "pull",
      material: "chrome",
      finish: "brushed",
      ...config
    };
    return createHardwareSchema(uuid5(), fullConfig, position, "Hardware");
  },
  ReactComponent: HardwareComponent
};

// src/Scene.tsx
import { Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment, GizmoHelper, GizmoViewcube } from "@react-three/drei";

// src/SceneRenderer.tsx
import React7, { useEffect as useEffect5, useRef as useRef2 } from "react";
import { observer as observer7 } from "mobx-react-lite";
import { TransformControls } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { Quaternion as Quaternion3, Vector3 } from "three";
import { Fragment, jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var meshRefsMap = /* @__PURE__ */ new Map();
var HardwareRenderer = observer7(({ component }) => {
  const store = useFurnitureStore();
  if (!meshRefsMap.has(component.id)) {
    meshRefsMap.set(component.id, React7.createRef());
  }
  const meshRef = meshRefsMap.get(component.id);
  const dims = component.dimensions || { width: 0.03, height: 0.03, depth: 0.02 };
  const color = component.material?.color || "#C0C0C0";
  const transform = component.layout?.transform || component.transform;
  const isSelected = store.selectedComponentIds.has(component.id);
  const isHovered = store.hoveredComponentId === component.id;
  const isHidden = isComponentHidden(component);
  const handleClick = (e) => {
    e.stopPropagation();
    const shiftKey = e.originalEvent?.shiftKey || false;
    store.selectComponent(component.id, shiftKey);
  };
  const handleContextMenu = (e) => {
    e.stopPropagation();
    if (!store.selectedComponentIds.has(component.id)) {
      store.selectComponent(component.id, false);
    }
    const event = new CustomEvent("component-rightclick", {
      detail: {
        componentId: component.id,
        clientX: e.originalEvent?.clientX || 0,
        clientY: e.originalEvent?.clientY || 0
      },
      bubbles: true
    });
    window.dispatchEvent(event);
  };
  const handlePointerOver = (e) => {
    e.stopPropagation();
    store.setHoveredComponent(component.id);
  };
  const handlePointerOut = () => {
    if (store.hoveredComponentId === component.id) {
      store.setHoveredComponent(null);
    }
  };
  if (isHidden) return null;
  return /* @__PURE__ */ jsxs4(
    "mesh",
    {
      ref: meshRef,
      position: [transform.position.x, transform.position.y, transform.position.z],
      visible: !isHidden,
      castShadow: true,
      receiveShadow: true,
      onClick: handleClick,
      onContextMenu: handleContextMenu,
      onPointerOver: handlePointerOver,
      onPointerOut: handlePointerOut,
      children: [
        /* @__PURE__ */ jsx9("boxGeometry", { args: [dims.width, dims.height, dims.depth] }),
        /* @__PURE__ */ jsx9(
          "meshStandardMaterial",
          {
            color: isSelected ? "#4A90E2" : isHovered ? "#6BA6D6" : color,
            metalness: 0.8,
            roughness: component.material?.finish === "polished" ? 0.1 : 0.4,
            emissive: isSelected ? "#1a4a90" : "#000000",
            emissiveIntensity: isSelected ? 0.2 : 0
          }
        )
      ]
    }
  );
});
var PanelRenderer = observer7(({ component }) => {
  const store = useFurnitureStore();
  if (!meshRefsMap.has(component.id)) {
    meshRefsMap.set(component.id, React7.createRef());
  }
  const meshRef = meshRefsMap.get(component.id);
  const geometry = component.geometry;
  const material = component.material;
  const transform = component.layout?.transform || component.transform;
  if (!geometry || !transform) {
    console.warn("PanelRenderer: missing geometry or transform", component.id, { geometry, transform });
    return null;
  }
  const position = transform.position;
  const rotation = transform.rotation;
  const scale = transform.scale || { x: 1, y: 1, z: 1 };
  const isSelected = store.selectedComponentIds.has(component.id);
  const isHovered = store.hoveredComponentId === component.id;
  const isHidden = isComponentHidden(component);
  const handleClick = (e) => {
    e.stopPropagation();
    const shiftKey = e.originalEvent?.shiftKey || false;
    store.selectComponent(component.id, shiftKey);
  };
  const handleContextMenu = (e) => {
    e.stopPropagation();
    if (!store.selectedComponentIds.has(component.id)) {
      store.selectComponent(component.id, false);
    }
    const event = new CustomEvent("component-rightclick", {
      detail: {
        componentId: component.id,
        clientX: e.originalEvent?.clientX || 0,
        clientY: e.originalEvent?.clientY || 0
      },
      bubbles: true
    });
    window.dispatchEvent(event);
  };
  const handlePointerOver = (e) => {
    e.stopPropagation();
    store.setHoveredComponent(component.id);
  };
  const handlePointerOut = () => {
    if (store.hoveredComponentId === component.id) {
      store.setHoveredComponent(null);
    }
  };
  if (isHidden) return null;
  return /* @__PURE__ */ jsxs4(
    "mesh",
    {
      ref: meshRef,
      position: [position.x, position.y, position.z],
      quaternion: [rotation.x, rotation.y, rotation.z, rotation.w],
      scale: [scale.x, scale.y, scale.z],
      visible: !isHidden,
      castShadow: true,
      receiveShadow: true,
      onClick: handleClick,
      onContextMenu: handleContextMenu,
      onPointerOver: handlePointerOver,
      onPointerOut: handlePointerOut,
      children: [
        /* @__PURE__ */ jsx9("boxGeometry", { args: [geometry.width, geometry.height, geometry.depth] }),
        /* @__PURE__ */ jsx9(
          "meshStandardMaterial",
          {
            color: isSelected ? "#4A90E2" : isHovered ? "#6BA6D6" : material?.color || "#D2B48C",
            roughness: material?.pbr_properties?.roughness || 0.8,
            metalness: material?.pbr_properties?.metalness || 0,
            emissive: isSelected ? "#1a4a90" : "#000000",
            emissiveIntensity: isSelected ? 0.2 : 0
          }
        )
      ]
    }
  );
});
var snapToGrid = (position, gridSize) => {
  return new Vector3(
    Math.round(position.x / gridSize) * gridSize,
    Math.round(position.y / gridSize) * gridSize,
    Math.round(position.z / gridSize) * gridSize
  );
};
var GroupRenderer = observer7(({ component }) => {
  const store = useFurnitureStore();
  if (!meshRefsMap.has(component.id)) {
    meshRefsMap.set(component.id, React7.createRef());
  }
  const groupRef = meshRefsMap.get(component.id);
  const transform = component.layout?.transform || component.transform;
  const isSelected = store.selectedComponentIds.has(component.id);
  const isHovered = store.hoveredComponentId === component.id;
  const isHidden = isComponentHidden(component);
  const getAllChildIds = (comp) => {
    const ids = [comp.id];
    if (comp.children) {
      Object.values(comp.children).forEach((child) => {
        ids.push(...getAllChildIds(child));
      });
    }
    return ids;
  };
  const childIds = getAllChildIds(component);
  const handleClick = (e) => {
    e.stopPropagation();
    const shiftKey = e.originalEvent?.shiftKey || false;
    store.selectComponent(component.id, shiftKey);
    childIds.forEach((childId) => {
      if (childId !== component.id) {
        store.selectComponent(childId, true);
      }
    });
  };
  const handleContextMenu = (e) => {
    e.stopPropagation();
    if (!store.selectedComponentIds.has(component.id)) {
      store.selectComponent(component.id, false);
      childIds.forEach((childId) => {
        if (childId !== component.id) {
          store.selectComponent(childId, true);
        }
      });
    }
    const event = new CustomEvent("component-rightclick", {
      detail: {
        componentId: component.id,
        clientX: e.originalEvent?.clientX || 0,
        clientY: e.originalEvent?.clientY || 0
      },
      bubbles: true
    });
    window.dispatchEvent(event);
  };
  const handlePointerOver = (e) => {
    e.stopPropagation();
    store.setHoveredComponent(component.id);
  };
  const handlePointerOut = () => {
    if (store.hoveredComponentId === component.id) {
      store.setHoveredComponent(null);
    }
  };
  if (!transform) {
    return /* @__PURE__ */ jsx9(
      "group",
      {
        ref: groupRef,
        visible: !isHidden,
        onClick: handleClick,
        onContextMenu: handleContextMenu,
        onPointerOver: handlePointerOver,
        onPointerOut: handlePointerOut,
        children: component.children && Object.values(component.children).map((child) => /* @__PURE__ */ jsx9(ComponentRenderer, { component: child }, child.id))
      }
    );
  }
  const { position, rotation, scale } = transform;
  if (isHidden) {
    return /* @__PURE__ */ jsx9(
      "group",
      {
        ref: groupRef,
        position: [position.x, position.y, position.z],
        quaternion: [rotation.x, rotation.y, rotation.z, rotation.w],
        scale: [scale.x, scale.y, scale.z],
        visible: false,
        children: component.children && Object.values(component.children).map((child) => /* @__PURE__ */ jsx9(ComponentRenderer, { component: child }, child.id))
      }
    );
  }
  const getBoundingBox = () => {
    if (!component.children || Object.keys(component.children).length === 0) return null;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    Object.values(component.children).forEach((child) => {
      const childTransform = child.layout?.transform || child.transform;
      if (childTransform) {
        const childPos = childTransform.position;
        const childGeom = child.geometry;
        if (childGeom) {
          const halfW = (childGeom.width || 0.1) / 2;
          const halfH = (childGeom.height || 0.1) / 2;
          const halfD = (childGeom.depth || 0.1) / 2;
          minX = Math.min(minX, childPos.x - halfW);
          minY = Math.min(minY, childPos.y - halfH);
          minZ = Math.min(minZ, childPos.z - halfD);
          maxX = Math.max(maxX, childPos.x + halfW);
          maxY = Math.max(maxY, childPos.y + halfH);
          maxZ = Math.max(maxZ, childPos.z + halfD);
        }
      }
    });
    if (minX === Infinity) return null;
    const width = maxX - minX;
    const height = maxY - minY;
    const depth = maxZ - minZ;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    return { width, height, depth, centerX, centerY, centerZ };
  };
  const bbox = getBoundingBox();
  return /* @__PURE__ */ jsxs4(
    "group",
    {
      ref: groupRef,
      position: [position.x, position.y, position.z],
      quaternion: [rotation.x, rotation.y, rotation.z, rotation.w],
      scale: [scale.x, scale.y, scale.z],
      visible: !isHidden,
      onClick: handleClick,
      onContextMenu: handleContextMenu,
      onPointerOver: handlePointerOver,
      onPointerOut: handlePointerOut,
      children: [
        component.children && Object.values(component.children).map((child) => /* @__PURE__ */ jsx9(ComponentRenderer, { component: child }, child.id)),
        isSelected && bbox && /* @__PURE__ */ jsxs4("mesh", { position: [bbox.centerX - position.x, bbox.centerY - position.y, bbox.centerZ - position.z], children: [
          /* @__PURE__ */ jsx9("boxGeometry", { args: [bbox.width + 0.02, bbox.height + 0.02, bbox.depth + 0.02] }),
          /* @__PURE__ */ jsx9(
            "meshBasicMaterial",
            {
              color: "#4A90E2",
              wireframe: true,
              transparent: true,
              opacity: 0.5
            }
          )
        ] }),
        isHovered && bbox && /* @__PURE__ */ jsxs4("mesh", { position: [bbox.centerX - position.x, bbox.centerY - position.y, bbox.centerZ - position.z], children: [
          /* @__PURE__ */ jsx9("boxGeometry", { args: [bbox.width + 0.02, bbox.height + 0.02, bbox.depth + 0.02] }),
          /* @__PURE__ */ jsx9(
            "meshBasicMaterial",
            {
              color: "#6BA6D6",
              wireframe: true,
              transparent: true,
              opacity: 0.3
            }
          )
        ] })
      ]
    }
  );
});
var ComponentRenderer = observer7(({ component }) => {
  if (!component) {
    return null;
  }
  if (component.type === "panel") {
    return /* @__PURE__ */ jsx9(PanelRenderer, { component });
  } else if (component.type === "group") {
    return /* @__PURE__ */ jsx9(GroupRenderer, { component });
  } else if (component.type === "hardware") {
    return /* @__PURE__ */ jsx9(HardwareRenderer, { component });
  }
  return null;
});
var SceneRenderer = observer7(() => {
  return /* @__PURE__ */ jsx9(ComponentRendererInner, {});
});
var ComponentRendererInner = observer7(() => {
  const store = useFurnitureStore();
  const controlsRef = useRef2(null);
  const { camera, gl } = useThree();
  const selectedComponentId = store.selectedComponentIds.size > 0 ? Array.from(store.selectedComponentIds)[0] : null;
  const selectedComponent = selectedComponentId ? store.components.get(selectedComponentId) : null;
  const isDraggingRef = useRef2(false);
  const initialParentPositionRef = useRef2(null);
  const handleObjectChange = (isFinalUpdate = false) => {
    if (!isDraggingRef.current && !isFinalUpdate) return;
    if (!selectedComponent || !controlsRef.current) return;
    const object = controlsRef.current.object;
    if (!object) return;
    const position = new Vector3();
    const quaternion = new Quaternion3();
    const scale = new Vector3();
    object.matrixWorld.decompose(position, quaternion, scale);
    let finalPosition = position;
    if (store.snapToGrid) {
      finalPosition = snapToGrid(position, store.gridSize);
      if (!finalPosition.equals(position)) {
        object.position.copy(finalPosition);
      }
    }
    const transform = selectedComponent.layout?.transform || selectedComponent.transform;
    if (transform) {
      const initialPosition = initialParentPositionRef.current || new Vector3(
        transform.position.x,
        transform.position.y,
        transform.position.z
      );
      const positionDelta = new Vector3(
        finalPosition.x - initialPosition.x,
        finalPosition.y - initialPosition.y,
        finalPosition.z - initialPosition.z
      );
      transform.position.x = finalPosition.x;
      transform.position.y = finalPosition.y;
      transform.position.z = finalPosition.z;
      transform.rotation.x = quaternion.x;
      transform.rotation.y = quaternion.y;
      transform.rotation.z = quaternion.z;
      transform.rotation.w = quaternion.w;
      transform.scale.x = scale.x;
      transform.scale.y = scale.y;
      transform.scale.z = scale.z;
    }
  };
  const handleDragStart = () => {
    isDraggingRef.current = true;
    if (selectedComponent) {
      const transform = selectedComponent.layout?.transform || selectedComponent.transform;
      if (transform) {
        initialParentPositionRef.current = new Vector3(
          transform.position.x,
          transform.position.y,
          transform.position.z
        );
      }
    }
  };
  const handleDragEnd = () => {
    if (isDraggingRef.current) {
      handleObjectChange(true);
      isDraggingRef.current = false;
      initialParentPositionRef.current = null;
    }
  };
  useEffect5(() => {
    if (selectedComponent && controlsRef.current && meshRefsMap.has(selectedComponent.id)) {
      const mesh = meshRefsMap.get(selectedComponent.id).current;
      if (mesh) {
        const transform = selectedComponent.layout?.transform || selectedComponent.transform;
        if (transform && mesh.position) {
          mesh.position.set(transform.position.x, transform.position.y, transform.position.z);
          mesh.quaternion.set(transform.rotation.x, transform.rotation.y, transform.rotation.z, transform.rotation.w);
          mesh.scale.set(
            transform.scale?.x || 1,
            transform.scale?.y || 1,
            transform.scale?.z || 1
          );
          isDraggingRef.current = false;
        }
      }
    }
  }, [selectedComponentId]);
  useEffect5(() => {
    const handleClick = (event) => {
      if (event.target === gl.domElement) {
        setTimeout(() => {
          if (!event.shiftKey) {
            store.clearSelection();
          }
        }, 0);
      }
    };
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [store, gl]);
  const rootComponents = Array.from(store.components.values()).filter((c) => !c.parent);
  if (rootComponents.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs4(Fragment, { children: [
    rootComponents.map((component) => {
      if (!component) return null;
      return /* @__PURE__ */ jsx9("group", { children: /* @__PURE__ */ jsx9(ComponentRenderer, { component }) }, component.id);
    }),
    selectedComponent && store.transformMode && meshRefsMap.has(selectedComponent.id) && meshRefsMap.get(selectedComponent.id).current && /* @__PURE__ */ jsx9(
      TransformControls,
      {
        ref: controlsRef,
        object: meshRefsMap.get(selectedComponent.id).current,
        mode: store.transformMode,
        onMouseDown: handleDragStart,
        onMouseUp: handleDragEnd,
        onChange: () => {
          if (isDraggingRef.current) {
            handleObjectChange(false);
          }
        },
        showX: true,
        showY: true,
        showZ: true
      }
    )
  ] });
});

// src/Scene.tsx
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var Scene = ({ children }) => {
  return /* @__PURE__ */ jsx10("div", { style: { width: "100%", height: "100%", position: "relative" }, children: /* @__PURE__ */ jsx10(
    Canvas,
    {
      shadows: true,
      camera: { position: [5, 5, 5], fov: 50 },
      style: { width: "100%", height: "100%" },
      children: /* @__PURE__ */ jsxs5(Suspense, { fallback: null, children: [
        /* @__PURE__ */ jsx10("color", { attach: "background", args: ["#1a1a1a"] }),
        /* @__PURE__ */ jsx10(Environment, { preset: "apartment" }),
        /* @__PURE__ */ jsx10("ambientLight", { intensity: 0.5 }),
        /* @__PURE__ */ jsx10("directionalLight", { position: [10, 10, 5], intensity: 1, castShadow: true }),
        /* @__PURE__ */ jsxs5("mesh", { rotation: [-Math.PI / 2, 0, 0], position: [0, 0, 0], receiveShadow: true, children: [
          /* @__PURE__ */ jsx10("planeGeometry", { args: [20, 20] }),
          /* @__PURE__ */ jsx10("meshStandardMaterial", { color: "#cccccc" })
        ] }),
        /* @__PURE__ */ jsx10("gridHelper", { args: [20, 20, "#888888", "#444444"] }),
        /* @__PURE__ */ jsx10(SceneRenderer, {}),
        children,
        /* @__PURE__ */ jsx10(OrbitControls, { makeDefault: true }),
        /* @__PURE__ */ jsx10(GizmoHelper, { alignment: "bottom-right", margin: [80, 80], children: /* @__PURE__ */ jsx10(GizmoViewcube, {}) })
      ] })
    }
  ) });
};
export {
  ComponentRendererInner,
  Door,
  Drawer,
  FurnitureStoreProvider,
  Group,
  Hardware,
  Material,
  Panel,
  Scene,
  SceneRenderer,
  SimpleCabinet,
  SimpleCabinetDefinition,
  SimpleCabinetFactory,
  useFurnitureStore
};
//# sourceMappingURL=index.mjs.map